import { forwardRef } from 'react'

import { Meter as MeterPrimitive } from '@base-ui/react/meter'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

export interface SegmentedBarSegment {
  value: number
  label?: string
  /** Tailwind bg-* class; defaults to the primary ramp by index. */
  color?: string
}

const DEFAULT_RAMP = [
  'bg-primary-100',
  'bg-primary-200',
  'bg-primary-300',
  'bg-primary'
]

const segmentedBarVariants = tv({
  slots: {
    root: 'flex w-full items-stretch',
    segment: 'relative h-full overflow-hidden rounded-full',
    track: 'size-full'
  },
  variants: {
    size: {
      sm: { root: 'h-1.5' },
      md: { root: 'h-2.5' }
    }
  },
  defaultVariants: { size: 'md' }
})

export type SegmentedBarProps = Omit<
  React.ComponentPropsWithRef<'div'>,
  'color'
> &
  VariantProps<typeof segmentedBarVariants> & {
    segments: SegmentedBarSegment[]
    total?: number
    /** Gap between segments, in px. */
    gap?: number
    /**
     * Minimum rendered width per non-zero segment, in px. Keeps the bar a
     * faithful part-to-whole view (segments stay proportional via flex-grow)
     * while guaranteeing a tiny-but-non-zero bucket never collapses to an
     * invisible sliver — e.g. $10M current next to a $1k 90+ bucket. The minimum
     * is "borrowed" from the dominant segment(s), so it nudges proportions
     * slightly rather than distorting magnitude the way a log scale would. Set
     * to `0` for strict proportional widths.
     */
    minSegment?: number
    /**
     * Gently scales the per-segment floor by magnitude so two tiny-but-different
     * buckets don't both pin to the same `minSegment` sliver (e.g. a $1.7k and a
     * $111 bucket reading identically). The floor grows as
     * `(value / smallestNonZero) ** minSegmentScale`, normalised so the smallest
     * non-zero bucket sits exactly at `minSegment` and capped at a few × that, so
     * it's a subtle "a bit more money → a bit wider" cue rather than a log/sqrt
     * that would distort the whole bar. `0` (default) keeps a flat floor for
     * every bucket. Try ~0.2–0.3 for a light touch.
     */
    minSegmentScale?: number
    /**
     * Index of the segment to keep emphasised; every other segment dims. Use to
     * mirror an external hover/selection (e.g. an aging tile). `null`/`undefined`
     * leaves all segments at full opacity.
     */
    activeIndex?: number | null
  }

export const SegmentedBar = forwardRef<HTMLDivElement, SegmentedBarProps>(
  (
    {
      segments,
      total,
      gap = 4,
      minSegment = 15,
      minSegmentScale = 0,
      size,
      activeIndex,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const styles = segmentedBarVariants({ size })
    const sum = total ?? segments.reduce((acc, s) => acc + s.value, 0)

    // The magnitude floor is normalised against the smallest non-zero bucket and
    // capped so it stays a subtle cue (never inflating a small bucket to rival a
    // genuinely large one). `minSegmentScale = 0` collapses this to a flat
    // `minSegment` for every bucket (x ** 0 === 1).
    const SOFT_FLOOR_CAP = 3
    const nonZeroValues = segments.filter(s => s.value > 0).map(s => s.value)
    const smallestNonZero = nonZeroValues.length
      ? Math.min(...nonZeroValues)
      : 1
    const minWidthFor = (value: number) =>
      Math.round(
        minSegment *
          Math.min(SOFT_FLOOR_CAP, (value / smallestNonZero) ** minSegmentScale)
      )

    return (
      <div
        ref={ref}
        data-slot='segmented-bar'
        className={cn(styles.root(), className)}
        style={{ gap, ...style }}
        {...props}
      >
        {segments.map((seg, i) =>
          // A zero-value segment has no visible bar, but would still occupy a
          // flex slot — and therefore a `gap` — in the row. Skip it. Returning
          // null (rather than filtering) preserves the original index so the
          // default color ramp stays aligned with non-zero segments.
          seg.value <= 0 ? null : (
            <MeterPrimitive.Root
              key={i}
              value={seg.value}
              max={sum || 1}
              aria-label={seg.label}
              data-slot='segmented-bar-segment'
              className={styles.segment()}
              // flex-grow encodes the true proportion; min-width is the visual
              // floor flexbox satisfies first, then distributes the remaining
              // space by proportion — so dominant buckets stay dominant and
              // tiny ones stay visible/hoverable.
              style={{
                flexGrow: seg.value,
                flexBasis: 0,
                minWidth: minWidthFor(seg.value)
              }}
            >
              {/* Color the Track (a plain wrapper). Base UI's Indicator would
                impose an inline width = value/max, which would fight the
                flex-grow proportion, so we don't render it. */}
              <MeterPrimitive.Track
                className={cn(
                  styles.track(),
                  seg.color ?? DEFAULT_RAMP[i % DEFAULT_RAMP.length],
                  // Opacity-only so there's no layout shift; instant for
                  // reduced-motion users.
                  'transition-opacity duration-150 ease-out motion-reduce:transition-none',
                  activeIndex != null && i !== activeIndex && 'opacity-40'
                )}
              />
            </MeterPrimitive.Root>
          )
        )}
      </div>
    )
  }
)
SegmentedBar.displayName = 'SegmentedBar'
